import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Eye, MoreHorizontal } from 'lucide-react';
import { CommonButton } from '@/components/ui/common-button';
import { CommonSearchInput } from '@/components/ui/common-search-input';
import { useToast } from '@/components/ui/use-toast';
import { FullScreenLoader, TableBlurLoader } from '@/components/ui/loader';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import PaginationComponent from '@/components/common/PaginationComponent';
import { formatTableText } from '@/lib/tableUtils';
import rakeMovementService from '@/services/rakeMovementService';
import CreateRakeMovementDialog from '../createRake/CreateRakeMovementDialog';
import EditRakeMovementDialog from '../editRake/EditRakeMovementDialog';
import ViewRakeMovementDialog from '../viewDetails/ViewRakeMovementDialog';

import { useAuth } from '@/contexts/AuthContext';

const RakeMovementsPage = () => {
  const { toast } = useToast();
  const { userProfile, userRole } = useAuth();
  const role = (userProfile?.role || userRole || '').toLowerCase();
  const canAccess = role === 'port_admin' || role === 'ipl_super_admin' || role === 'ipl_admin';

  const [rakeMovements, setRakeMovements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const pageSize = 10;

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [selectedRake, setSelectedRake] = useState(null);

  const loadRakeMovements = async () => {
    try {
      setLoading(true);
      const skip = (currentPage - 1) * pageSize;
      const params = { skip, limit: pageSize };
      if (searchQuery) params.rr_number = searchQuery;

      const res = await rakeMovementService.getRakeMovements(params);
      const list = Array.isArray(res) ? res : res?.data || [];
      const count = Array.isArray(res) ? res.length : res?.total || list.length;

      setRakeMovements(list);
      setTotalItems(count);
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to load rake trips.', variant: 'destructive' });
    } finally {
      setLoading(false);
      setIsInitialLoad(false);
    }
  };

  useEffect(() => {
    loadRakeMovements();
  }, [currentPage, searchQuery]);

  const handleCreate = async (rakeData) => {
    try {
      await rakeMovementService.createRakeMovement(rakeData);
      toast({ title: 'Success', description: 'Rake Trip created successfully.' });
      setIsCreateOpen(false);
      loadRakeMovements();
    } catch (err) {
      toast({ title: 'Error', description: err?.message || 'Failed to create rake trip.', variant: 'destructive' });
    }
  };

  const handleUpdate = async (rakeData) => {
    try {
      await rakeMovementService.updateRakeMovement(selectedRake.id, rakeData);
      toast({ title: 'Success', description: 'Rake Trip updated successfully.' });
      setIsEditOpen(false);
      setSelectedRake(null);
      loadRakeMovements();
    } catch (err) {
      toast({ title: 'Error', description: err?.message || 'Failed to update rake trip.', variant: 'destructive' });
    }
  };

  if (isInitialLoad) {
    return <FullScreenLoader isLoading={isInitialLoad} />;
  }

  const totalPages = Math.ceil(totalItems / pageSize) || 1;

  return (
    <div>
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-lg font-semibold tracking-tight text-foreground">
            Rake Trips
          </h1>
          <p className="text-xs text-muted-foreground">
            Manage, schedule, and track railway bulk freight rake movements.
          </p>
        </div>
        <CommonButton onClick={() => setIsCreateOpen(true)} variant="default" icon={Plus}>
          Create Rake Trip
        </CommonButton>
      </div>

      <div className="mb-4 w-full mt-4">
        <div className="md:w-1/3">
          <CommonSearchInput
            placeholder="Search by trip number, RR number..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onSearch={(val) => { setSearchQuery(val); setCurrentPage(1); }}
            maxLength={50}
          />
        </div>
      </div>

      <TableBlurLoader isLoading={loading} className="mt-4">
        <div className="border border-border p-0.5 rounded-[16px] bg-card w-full transition-all transform">
          <div className="border border-border py-4 rounded-[16px] bg-background">
            {rakeMovements.length > 0 ? (
              <div className="rounded-md border overflow-hidden w-full">
                <div className="max-h-[calc(100vh-280px)] overflow-auto w-full">
                  <Table>
                    <TableHeader className="sticky top-0 z-10">
                      <TableRow>
                        <TableHead className="h-10 px-4 py-1">Trip Number</TableHead>
                        <TableHead className="h-10 px-4 py-1">Port</TableHead>
                        <TableHead className="h-10 px-4 py-1">State / District</TableHead>
                        <TableHead className="h-10 px-4 py-1">Rake Point</TableHead>
                        {/* <TableHead className="h-10 px-4 py-1 min-w-[130px]">NIC Trip Start</TableHead>
                        <TableHead className="h-10 px-4 py-1 min-w-[130px]">NIC Trip End</TableHead>
                        <TableHead className="h-10 px-4 py-1 min-w-[130px]">NIC Trip Close</TableHead> */}
                        <TableHead className="h-10 px-4 py-1 text-right min-w-[100px]">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rakeMovements.map((rake) => (
                        <TableRow key={rake.id} className="hover:bg-transparent border-border">
                          <TableCell className="h-10 px-4 py-1 font-medium">{formatTableText(rake.trip_number)}</TableCell>
                          <TableCell className="h-10 px-4 py-1">
                            {formatTableText(rake.port?.port_name || rake.port?.name)}
                            {rake.port?.port_code && <span className="text-muted-foreground ml-1 uppercase">({rake.port.port_code})</span>}
                          </TableCell>
                          <TableCell className="h-10 px-4 py-1">
                            <div>{formatTableText(rake.state?.name)}</div>
                            <div className="text-muted-foreground">{formatTableText(rake.district?.name)}</div>
                          </TableCell>
                          <TableCell className="h-10 px-4 py-1">
                            {formatTableText(rake.rake_point?.name)}
                            {rake.rake_point?.code && <span className="text-muted-foreground ml-1">({rake.rake_point.code})</span>}
                          </TableCell>
                          {/* <TableCell className="h-10 px-4 py-1 text-muted-foreground">--</TableCell>
                          <TableCell className="h-10 px-4 py-1 text-muted-foreground">--</TableCell>
                          <TableCell className="h-10 px-4 py-1 text-muted-foreground">--</TableCell> */}
                          <TableCell className="h-10 px-4 py-1 text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" className="h-8 w-8 p-0">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => { setSelectedRake(rake); setIsViewOpen(true); }}>
                                  <Eye className="mr-2 h-4 w-4" />
                                  View Details
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => { setSelectedRake(rake); setIsEditOpen(true); }}>
                                  <Pencil className="mr-2 h-4 w-4" />
                                  Edit Rake Trip
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            ) : (
              <div className="flex justify-center items-center h-64 text-muted-foreground">
                No rake trips available
              </div>
            )}
            <PaginationComponent
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
              className="border-t mt-4"
            />
          </div>
        </div>
      </TableBlurLoader>

      <CreateRakeMovementDialog
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        onCreateRakeMovement={handleCreate}
      />

      <ViewRakeMovementDialog
        open={isViewOpen}
        onOpenChange={(open) => { setIsViewOpen(open); if (!open) setSelectedRake(null); }}
        rakeData={selectedRake}
      />

      {selectedRake && (
        <EditRakeMovementDialog
          open={isEditOpen}
          onOpenChange={(open) => { setIsEditOpen(open); if (!open) setSelectedRake(null); }}
          rakeData={selectedRake}
          onUpdateRakeMovement={handleUpdate}
        />
      )}
    </div>
  );
};

export default RakeMovementsPage;
